import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  ArrowLeft, Search, Check, ChevronRight,
  Car, Home as HomeIcon, Briefcase, Sofa, Armchair, Users, 
  Key, Building, Bed, Package, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryTranslations = {
  'Motors': 'السيارات',
  'Property': 'العقارات',
  'Jobs': 'الوظائف',
  'Classifieds': 'إعلانات متنوعة',
  'Community': 'المجتمع',
  'Furniture & Garden': 'الأثاث والحدائق',
  'Used Cars': 'سيارات مستعملة',
  'New Cars': 'سيارات جديدة',
  'Rental Cars': 'سيارات للإيجار',
  'Motorcycles': 'دراجات نارية',
  'Auto Accessories': 'إكسسوارات السيارات',
  'Heavy Vehicles': 'مركبات ثقيلة',
  'Boats': 'قوارب',
  'Number Plates': 'لوحات أرقام',
  'Property for Rent': 'عقارات للإيجار',
  'Property for Sale': 'عقارات للبيع',
  'Off-Plan Properties': 'عقارات على الخريطة',
  'Rooms for Rent': 'غرف للإيجار',
};

const categoryIcons = {
  'motors': Car,
  'property-rent': Key,
  'property-sale': HomeIcon,
  'off-plan': Building,
  'rooms': Bed,
  'jobs': Briefcase,
  'classifieds': Sofa,
  'furniture': Armchair,
  'community': Users,
};

// Removed per-category colors — using unified dark/red theme

const CategorySelectPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (categorySlug && categories.length > 0) {
      const cat = categories.find(c => c.slug === categorySlug);
      if (cat) {
        setSelectedCategory(cat);
        setSubcategories(cat.subcategories || []);
      }
    }
  }, [categorySlug, categories]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const translateName = (name) => {
    if (i18n.language === 'ar' && categoryTranslations[name]) {
      return categoryTranslations[name];
    }
    return name;
  };

  const handleCategorySelect = (category) => {
    if (category.subcategories && category.subcategories.length > 0) {
      navigate(`/category-select/${category.slug}`);
    } else {
      navigate(`/search?category=${category.id}`);
    }
  };

  const handleSubcategorySelect = (subId) => {
    setSelectedSubcategory(subId);
    if (subId === 'all') {
      navigate(`/search?category=${selectedCategory.id}`);
    } else {
      navigate(`/search?subcategory=${subId}`);
    }
  };

  const getIcon = (slug) => categoryIcons[slug] || Package;

  const filteredCategories = categories.filter(cat => 
    !searchQuery || translateName(cat.name).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSubs = subcategories.filter(sub =>
    !searchQuery || translateName(sub.name).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const title = selectedCategory ? translateName(selectedCategory.name) : (i18n.language === 'ar' ? 'اختر الفئة' : 'Select Category');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7f8fa] flex items-center justify-center">
        <div className="w-9 h-9 border-[3px] border-[#E31C25] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fa] pb-24" data-testid="category-select-page">
      {/* Dark Header */}
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-5xl mx-auto px-4 pt-4 pb-6 relative z-10">
          {/* Nav row */}
          <div className="flex items-center justify-between mb-5">
            <button 
              onClick={() => navigate(-1)} 
              className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <button 
              onClick={() => setShowSearch(!showSearch)} 
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${showSearch ? 'bg-[#E31C25]' : 'bg-white/10 hover:bg-white/20'}`}
              data-testid="search-toggle-btn"
            >
              <Search className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Title */}
          <div className="animate-fade-up">
            <h1 className="text-2xl md:text-3xl font-extrabold text-white font-heading tracking-tight">{title}</h1>
            {!selectedCategory && (
              <p className="text-white/50 text-sm mt-1">
                {i18n.language === 'ar' ? 'تصفح جميع الفئات' : 'Browse all categories'}
              </p>
            )}
            {selectedCategory && (
              <p className="text-white/50 text-sm mt-1">
                {filteredSubs.length} {i18n.language === 'ar' ? 'خيار' : filteredSubs.length === 1 ? 'option' : 'options'}
              </p>
            )}
          </div>

          {/* Search bar */}
          {showSearch && (
            <div className="mt-4 animate-fade-up">
              <div className="flex items-center gap-2.5 px-4 py-3 bg-white rounded-xl shadow-lg">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={i18n.language === 'ar' ? 'ابحث في الفئات...' : 'Search categories...'}
                  className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder-gray-400"
                  autoFocus
                  data-testid="category-search-input"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {selectedCategory ? (
          /* Subcategory View */
          <div>
            {/* All in Category */}
            <button
              onClick={() => handleSubcategorySelect('all')}
              className="w-full flex items-center justify-between px-4 py-3.5 mb-2 bg-gradient-to-r from-[#E31C25] to-[#b91420] rounded-2xl shadow-sm group animate-fade-up"
              data-testid="subcategory-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-semibold text-sm">
                  {i18n.language === 'ar' ? `الكل في ${translateName(selectedCategory.name)}` : `All in ${translateName(selectedCategory.name)}`}
                </span>
              </div>
              <ChevronRight className="w-5 h-5 text-white/70 group-hover:translate-x-0.5 transition-transform" />
            </button>

            {/* Subcategory list */}
            <div className="space-y-1.5">
              {filteredSubs.map((sub, index) => (
                <button
                  key={sub.id}
                  onClick={() => handleSubcategorySelect(sub.id)}
                  className={`animate-fade-up stagger-${Math.min(index + 1, 9)} w-full flex items-center justify-between px-4 py-3.5 bg-white rounded-2xl border border-gray-100 hover:border-[#E31C25]/20 hover:bg-red-50/30 transition-all group`}
                  data-testid={`subcategory-${sub.slug}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-gray-800 font-medium text-sm">{translateName(sub.name)}</span>
                    {sub.isNew && (
                      <span className="px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">
                        NEW
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedSubcategory === sub.id && (
                      <Check className="w-4 h-4 text-[#E31C25]" />
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#E31C25] group-hover:translate-x-0.5 transition-all" />
                  </div>
                </button>
              ))}
            </div>

            {filteredSubs.length === 0 && searchQuery && (
              <div className="text-center py-12">
                <Search className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">{i18n.language === 'ar' ? 'لا توجد نتائج' : 'No results found'}</p>
              </div>
            )}
          </div>
        ) : (
          /* Main Category Grid */
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {filteredCategories.map((cat, index) => {
              const Icon = getIcon(cat.slug);
              const subCount = cat.subcategories?.length || 0;

              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat)}
                  className={`animate-scale-in stagger-${Math.min(index + 1, 9)} relative bg-white rounded-2xl p-4 border border-gray-100 hover:border-[#E31C25]/30 hover:shadow-md transition-all duration-200 group text-left`}
                  data-testid={`category-${cat.slug}`}
                >
                  <div className="w-12 h-12 bg-[#FEE2E2] rounded-2xl flex items-center justify-center mb-3 group-hover:bg-[#E31C25] group-hover:scale-110 transition-all">
                    <Icon className="w-6 h-6 text-[#E31C25] group-hover:text-white transition-colors" strokeWidth={2} />
                  </div>
                  <h3 className="font-bold text-gray-800 text-sm leading-tight mb-1">{translateName(cat.name)}</h3>
                  {subCount > 0 && (
                    <p className="text-[11px] text-gray-400 font-medium">
                      {subCount} {i18n.language === 'ar' ? 'فئة فرعية' : subCount === 1 ? 'subcategory' : 'subcategories'}
                    </p>
                  )}
                  <ChevronRight className="absolute top-4 right-3 w-4 h-4 text-gray-200 group-hover:text-[#E31C25] group-hover:translate-x-0.5 transition-all" />
                </button>
              );
            })}
          </div>
        )}

        {!selectedCategory && filteredCategories.length === 0 && searchQuery && (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm font-medium">{i18n.language === 'ar' ? 'لا توجد فئات مطابقة' : 'No matching categories'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategorySelectPage;
